import { Box, Flex, Input, Checkbox } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { useAppToast } from '../../../helpers'
import { CommercialConsultantService } from '../../../services/CommercialConsultant/CommercialConsultantService'
import { DutyPlannerService } from '../../../services/DutyPlanner/DutyPlannerService'
import { DutyPlannerInput } from '../../../services/DutyPlanner/types'

interface ICommercialConsultantFormModalProps {
  isOpen: boolean
  onClose: () => void
  id: number | null
}

export default function DutyPlannerFormModal({ isOpen, onClose, id }: ICommercialConsultantFormModalProps) {
  const service = new DutyPlannerService()
  const commercialConsultantService = new CommercialConsultantService()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<DutyPlannerInput>({
    title: '',
    date: '',
    has_duty: true,
    consultant_ids: []
  })
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})
  const [commercialConsultants, setCommercialConsultants] = useState<
    { id: number; name: string; show_on_forms: boolean }[]
  >([])

  useEffect(() => {
    commercialConsultantService.list().then((consultants) => {
      setCommercialConsultants(consultants.map((c) => ({ id: c.id, name: c.name, show_on_forms: c.show_on_forms })))
    })
  }, [])

  useEffect(() => {
    if (id && isOpen) {
      service
        .get(id)
        .then((data) => {
          setForm({
            title: data.title,
            date: data.date.split('T')[0],
            has_duty: data.has_duty,
            consultant_ids: data.consultants.map((c) => c.id)
          })
          setFormErrors({})
        })
        .catch((err) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const error = err as any
          if (error.response?.status === 404) {
            toast.error('Feriado/Plantão não encontrado')
          } else {
            toast.error('Erro ao carregar feriado/plantão')
          }
          onClose()
        })
    } else {
      setForm({
        title: '',
        date: '',
        has_duty: true,
        consultant_ids: []
      })
    }
  }, [id, isOpen])

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSaving(true)

    if (form.has_duty && form.consultant_ids.length === 0) {
      toast.warning('Selecione pelo menos um consultor')
      setSaving(false)
      return
    }

    try {
      if (id) {
        await service.update(id, form)
        toast.success('Feriado/Plantão atualizado com sucesso')
      } else {
        await service.create(form)
        toast.success('Feriado/Plantão cadastrado com sucesso')
      }
      onClose()
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any
      const validationErrors: Record<string, string[]> | undefined = error.response?.data?.errors
      const backendMessage: string | undefined = error.response?.data?.message

      if (error.response?.status === 422 && validationErrors) {
        setFormErrors(validationErrors)
        const firstError = Object.values(validationErrors)[0]?.[0]
        toast.error(firstError ?? 'Verifique os campos destacados.', err)
      } else {
        toast.error(backendMessage ?? 'Erro ao salvar feriado/plantão', err)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${id ? 'Editar' : 'Cadastrar'} Feriado/Plantão`}
      onSubmit={handleSave}
      isLoading={saving}
      isConfirmDisabled={saving}
    >
      <Flex direction="column" gap={3}>
        <Box>
          <FormField label="Título (opcional)" error={formErrors.title?.[0]}>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} disabled={saving} />
          </FormField>
        </Box>
        <Box>
          <FormField label="Data" error={formErrors.date?.[0]} isRequired>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              disabled={saving}
            />
          </FormField>
        </Box>
        <Box>
          <FormField error={formErrors.has_duty?.[0]}>
            <Checkbox
              isChecked={form.has_duty}
              onChange={(e) => setForm({ ...form, has_duty: e.target.checked })}
              isDisabled={saving}
            >
              Possui plantão?
            </Checkbox>
          </FormField>
        </Box>
        {form.has_duty && (
          <Box>
            <FormField
              label="Consultores de Plantão"
              error={formErrors.consultant_ids?.[0] ?? formErrors['consultant_ids.0']?.[0]}
            >
              <Flex
                direction="column"
                gap={2}
                maxH="200px"
                overflowY="auto"
                border="1px solid #eee"
                borderRadius="md"
                p={2}
              >
                {/* Show consultants visible on lead forms + any already-selected ones (so they remain visible while checked) */}
                {commercialConsultants
                  .filter((c) => c.show_on_forms || form.consultant_ids.includes(c.id))
                  .map((consultant) => (
                    <Checkbox
                      key={consultant.id}
                      isChecked={form.consultant_ids.includes(consultant.id)}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setForm((prev) => ({
                          ...prev,
                          consultant_ids: checked
                            ? [...prev.consultant_ids, consultant.id]
                            : prev.consultant_ids.filter((id) => id !== consultant.id)
                        }))
                      }}
                      isDisabled={saving}
                    >
                      {consultant.name}
                    </Checkbox>
                  ))}
              </Flex>
            </FormField>
          </Box>
        )}
      </Flex>
    </FormModal>
  )
}
